import { useMutation, useApolloClient } from '@apollo/client';
import type * as React from 'react';

import type { SocialTrackingMetadataInput } from '@xing-com/crate-common-graphql-types';
import type { SocialInteractionTargetFragment } from '@xing-com/crate-utils-social-interactions';
import {
  SocialCreateReactionDocument,
  SocialInteractionTargetFragmentDoc,
  SocialDeleteReactionDocument,
} from '@xing-com/crate-utils-social-interactions';

import { getSocialTrackingMetaData } from '../..';
import { LikeButton } from '../like-button/like-button';

export type LikeReactionButtonProps = {
  backendTrackingMetaData?: SocialTrackingMetadataInput;
  hasLike: boolean;
  label: React.ReactNode;
  onCompleted?(data: unknown): void;
  onError?(): void;
  propSocialInteractionId?: string;
  urn: string;
  [props: string]: unknown;
};

export const LikeReactionButton: React.FC<LikeReactionButtonProps> = ({
  backendTrackingMetaData = { trackingTokens: [], page: '', position: '' },
  hasLike,
  label,
  onCompleted = () => undefined,
  onError = () => undefined,
  propSocialInteractionId,
  urn,
  ...props
}) => {
  const MUTATION = hasLike
    ? SocialDeleteReactionDocument
    : SocialCreateReactionDocument;

  const apollo = useApolloClient();
  const [reactionsMutation, { loading }] = useMutation(MUTATION, {
    onError,
    onCompleted,
  });

  const handleOptimisticUpdate = (): void => {
    const id = `SocialInteractionTarget:${urn}`;

    const interactions = apollo.readFragment({
      id,
      fragment: SocialInteractionTargetFragmentDoc,
      fragmentName: 'SocialInteractionTarget',
    });

    const nextInteraction = interactions?.userReactionType
      ? {
          ...interactions,
          userReactionType: null,
          reactionsCount:
            interactions.reactionsCount === 0
              ? 0
              : interactions.reactionsCount - 1,
        }
      : {
          ...interactions,
          userReactionType: 'LIKE',
          reactionsCount: (interactions?.reactionsCount ?? 0) + 1,
        };

    apollo.writeFragment({
      id,
      fragment: SocialInteractionTargetFragmentDoc,
      fragmentName: 'SocialInteractionTarget',
      data: nextInteraction as SocialInteractionTargetFragment,
    });
  };

  const handleOnClick = (): void => {
    if (!loading) {
      handleOptimisticUpdate();
      reactionsMutation({
        variables: {
          urn,
          ...(hasLike
            ? {}
            : getSocialTrackingMetaData({
                propSocialInteractionId,
                backendTrackingMetaData,
              })),
        },
      });
    }
  };

  return (
    <LikeButton {...{ label, hasLike }} onClick={handleOnClick} {...props} />
  );
};
